// Pure editing model for the score: the inverse of the engraver's placement
// (screen position -> pitch/beat). Kept free of React so the interactions are
// unit-testable as data. See SPEC.md §9. The melody edit operations themselves
// live in music/melodyEdit.ts (they operate on the chart, the score model).

import { clefBottomLine, type Clef } from './pitch';

const STEP_SEMITONE = [0, 2, 4, 5, 7, 9, 11]; // C D E F G A B

/**
 * Inverse of pitch.staffOffset: snap a vertical position (staff-spaces below
 * the staff's top line) to the nearest line/space and return its natural
 * MIDI pitch. On the default treble staff, y=0 is the top line (F5) and
 * y=4 the bottom line (E4); other clefs anchor on their own bottom line.
 */
export function pitchFromStaffY(yBelowTop: number, clef: Clef = 'treble'): number {
  const offset = 4 - yBelowTop; // staff-spaces above the bottom line
  const step = Math.round(offset / 0.5); // diatonic steps above the bottom line
  const diatonic = clefBottomLine(clef) + step;
  const octave = Math.floor(diatonic / 7);
  const letterIdx = ((diatonic % 7) + 7) % 7;
  return (octave + 1) * 12 + STEP_SEMITONE[letterIdx];
}

/** Snap a horizontal fraction (0..1 across a bar) to a beat on a grid. */
export function snapBeat(fraction: number, beats: number, grid = 1): number {
  const clamped = Math.min(1, Math.max(0, fraction));
  const raw = clamped * beats; // 0..beats
  const snapped = Math.round(raw / grid) * grid;
  // A selected value may be longer than a pickup. In that case beat 1 is the
  // only legal onset; never let the upper bound fall below it.
  return Math.min(1 + Math.max(0, beats - grid), Math.max(1, 1 + snapped));
}
